import type { TFunction } from "i18next";
import type { AdOffer, AdDuration, AdAspect, AdStillAspect } from "@/components/landing/director-studio/types";

// Commercials & Ads — the frontend's ONLY knowledge of the offer catalogue.
//
// Prices come from GET /api/web/ads/config (docs/ads-tool-contract.md §9) and
// are never hardcoded in a component. The constants below exist for exactly
// one situation: the signed-out sales pitch, where /config (JWT-gated) cannot
// be read. They mirror shared/config/ads.js at the time of writing and are
// overwritten by the server's answer the moment one arrives.

export interface AdsStockVoice { id: string; name: string; language: string; note?: string }
export interface AdsSavedVoice { id: string; name: string; language: string; sample_url: string; created_at?: string }
export interface AdsStockTrack { id: string; title: string; seconds: number; mood: string; preview_url?: string }

export interface AdsConfig {
  offers: AdOffer[];
  durations: AdDuration[];
  aspects: AdAspect[];
  still_aspects: AdStillAspect[];
  still_counts: Array<8 | 16>;
  credits: {
    spot: Record<string, number>;
    still: number;
    /** spot + 8 stills */
    package: Record<string, number>;
  };
  voice: { stock: AdsStockVoice[]; clone_max_seconds: number; clone_min_seconds: number };
  music: { stock: AdsStockTrack[] };
}

export const ADS_FALLBACK_CONFIG: AdsConfig = {
  offers: ["spot", "stills", "package"],
  durations: [15, 30, 60],
  aspects: ["9:16", "16:9", "1:1"],
  still_aspects: ["1:1", "4:5", "9:16", "16:9"],
  still_counts: [8, 16],
  credits: {
    spot: { "15": 35, "30": 65, "60": 125 },
    still: 3,
    package: { "15": 59, "30": 89, "60": 149 },
  },
  voice: { stock: [], clone_max_seconds: 60, clone_min_seconds: 8 },
  music: { stock: [] },
};

export function adsAuthHeaders(json = true): Record<string, string> {
  const headers: Record<string, string> = {};
  let token: string | null = null;
  try { token = localStorage.getItem("auth_token"); } catch { token = null; }
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (json) headers["Content-Type"] = "application/json";
  return headers;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null;
}

/** Merge a server config over the fallback so a partial answer never leaves a
 *  hole the UI then reads as `undefined`. */
export function mergeAdsConfig(raw: unknown): AdsConfig {
  if (!isRecord(raw)) return ADS_FALLBACK_CONFIG;
  const credits = isRecord(raw.credits) ? raw.credits : {};
  const voice = isRecord(raw.voice) ? raw.voice : {};
  const music = isRecord(raw.music) ? raw.music : {};
  const list = <T,>(v: unknown, fallback: T[]): T[] => (Array.isArray(v) && v.length ? (v as T[]) : fallback);
  return {
    offers: list(raw.offers, ADS_FALLBACK_CONFIG.offers),
    durations: list(raw.durations, ADS_FALLBACK_CONFIG.durations),
    aspects: list(raw.aspects, ADS_FALLBACK_CONFIG.aspects),
    still_aspects: list(raw.still_aspects, ADS_FALLBACK_CONFIG.still_aspects),
    still_counts: list(raw.still_counts, ADS_FALLBACK_CONFIG.still_counts),
    credits: {
      spot: isRecord(credits.spot) ? { ...ADS_FALLBACK_CONFIG.credits.spot, ...(credits.spot as Record<string, number>) } : ADS_FALLBACK_CONFIG.credits.spot,
      still: typeof credits.still === "number" ? credits.still : ADS_FALLBACK_CONFIG.credits.still,
      package: isRecord(credits.package) ? { ...ADS_FALLBACK_CONFIG.credits.package, ...(credits.package as Record<string, number>) } : ADS_FALLBACK_CONFIG.credits.package,
    },
    voice: {
      stock: Array.isArray(voice.stock) ? (voice.stock as AdsStockVoice[]) : [],
      clone_max_seconds: typeof voice.clone_max_seconds === "number" ? voice.clone_max_seconds : ADS_FALLBACK_CONFIG.voice.clone_max_seconds,
      clone_min_seconds: typeof voice.clone_min_seconds === "number" ? voice.clone_min_seconds : ADS_FALLBACK_CONFIG.voice.clone_min_seconds,
    },
    music: { stock: Array.isArray(music.stock) ? (music.stock as AdsStockTrack[]) : [] },
  };
}

/** GET /api/web/ads/config. Resolves to the fallback (flagged) when the call
 *  fails — signed out, offline, or the service is not mounted yet. */
export async function fetchAdsConfig(): Promise<{ config: AdsConfig; fromServer: boolean }> {
  try {
    const r = await fetch("/api/web/ads/config", { headers: adsAuthHeaders(false) });
    if (!r.ok) return { config: ADS_FALLBACK_CONFIG, fromServer: false };
    const j: unknown = await r.json();
    return { config: mergeAdsConfig(j), fromServer: true };
  } catch {
    return { config: ADS_FALLBACK_CONFIG, fromServer: false };
  }
}

/** Exact credits for an offer, straight from the config ladder. */
export function adsCreditsFor(config: AdsConfig, offer: AdOffer, duration: AdDuration, stillCount: 8 | 16): number {
  const key = String(duration);
  if (offer === "spot") return config.credits.spot[key] ?? 0;
  if (offer === "stills") return config.credits.still * stillCount;
  // package = spot + 8 stills, no discount; 16 stills adds the extra 8.
  const base = config.credits.package[key] ?? ((config.credits.spot[key] ?? 0) + config.credits.still * 8);
  return base + (stillCount > 8 ? config.credits.still * (stillCount - 8) : 0);
}

/** POST /api/web/upload with the ad type strings from the contract
 *  (ad_product, ad_logo, ad_selfie, ad_voice, audio). Same call the Story
 *  flow makes; returns the public URL the server hands back. */
export async function uploadAdFile(t: TFunction, file: File, type: "ad_product" | "ad_logo" | "ad_selfie" | "ad_voice" | "audio"): Promise<string> {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("type", type);
  const r = await fetch("/api/web/upload", { method: "POST", headers: adsAuthHeaders(false), body: fd });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || !j.url) throw new Error(`${file.name}: ${j.message || j.error || t("ads.uploadFailed")}`);
  return j.url as string;
}

/** Error shape from every /api/web/ads route: `{ error, message }`. */
export function adsErrorMessage(t: TFunction, status: number, body: unknown, fallbackKey: string): string {
  const err = isRecord(body) ? body : {};
  if (err.error === "insufficient_credits" || status === 402) return t("ads.insufficientCredits");
  if (err.error === "render_service_down" || status === 503) return t("ads.serviceDown");
  // A person in the ad only through an avatar (FACELOCK_CREW_REQUIRED), in the reader's language.
  if (err.error === "facelock_required") return t("ads.errors.facelock_required");
  // An avatar whose video files still train (stills first), or never trained: an ad is video.
  if (err.error === "avatar_video_pending" || err.error === "avatar_video_failed") return t(`ads.errors.${err.error}`);
  if (typeof err.message === "string" && err.message.trim()) return err.message;
  if (typeof err.error === "string" && err.error.trim()) return err.error;
  return t(fallbackKey);
}
